// Minimal Open Location Code decoder, full codes only: 8 characters before the "+".
// Shortened codes ("JGWV+XQ") are deliberately rejected - recovering one needs a reference
// location within ~50 km, which we have no way to obtain per merchant.
// Digits past the 10th (the grid refinement step) are ignored: 10 digits is ~14 m, far below
// the zoom level we show.
const CODE_ALPHABET = "23456789CFGHJMPQRVWX"
const SEPARATOR = "+"
const SEPARATOR_POSITION = 8
const PAIR_RESOLUTIONS = [20, 1, 0.05, 0.0025, 0.000125]
const LATITUDE_BLOCKS = 9
const LONGITUDE_BLOCKS = 18

export type PlusCodeLocation = Readonly<{
    latitude: number
    longitude: number
}>

export function decodeFullPlusCode(code: string): PlusCodeLocation | null {
    const normalized = code.trim().toUpperCase()
    if (normalized.indexOf(SEPARATOR) !== SEPARATOR_POSITION) return null

    const digits = normalized.slice(0, SEPARATOR_POSITION) + normalized.slice(SEPARATOR_POSITION + 1)
    if (digits.length < PAIR_RESOLUTIONS.length * 2) return null

    let latitude = -90
    let longitude = -180
    let resolution = 0

    for (let pair = 0; pair < PAIR_RESOLUTIONS.length; pair++) {
        const latitudeIndex = CODE_ALPHABET.indexOf(digits[pair * 2])
        const longitudeIndex = CODE_ALPHABET.indexOf(digits[pair * 2 + 1])
        // also rejects the "0" of padded codes, which is not part of the alphabet
        if (latitudeIndex < 0 || longitudeIndex < 0) return null
        if (pair === 0 && (latitudeIndex >= LATITUDE_BLOCKS || longitudeIndex >= LONGITUDE_BLOCKS)) return null

        resolution = PAIR_RESOLUTIONS[pair]
        latitude += latitudeIndex * resolution
        longitude += longitudeIndex * resolution
    }

    return { latitude: latitude + resolution / 2, longitude: longitude + resolution / 2 }
}

// A shortened code ("9FWC+8X") cannot be decoded, but Google Maps resolves it when searched
// together with a locality, so it is still worth putting in a search query. Anything malformed
// is rejected instead - passing it on would only skew the search away from the address.
export function isSearchablePlusCode(code: string): boolean {
    const normalized = code.trim().toUpperCase()
    const separatorIndex = normalized.indexOf(SEPARATOR)

    // codes are built from pairs, so the separator always sits on an even index
    if (separatorIndex < 2 || separatorIndex > SEPARATOR_POSITION || separatorIndex % 2 !== 0) return false
    if (normalized.indexOf(SEPARATOR, separatorIndex + 1) >= 0) return false
    if (normalized.length - separatorIndex - 1 < 2) return false

    const digits = normalized.slice(0, separatorIndex) + normalized.slice(separatorIndex + 1)
    if (Array.from(digits).some((digit) => CODE_ALPHABET.indexOf(digit) < 0)) return false

    // a full-length code must also name a real 20 degree block, which decoding checks
    return separatorIndex < SEPARATOR_POSITION || decodeFullPlusCode(normalized) !== null
}
